import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {parseISO} from 'date-fns/parseISO';
import {subDays} from 'date-fns/subDays';

import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewDatePickerField,
  showModal,
} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {StatusIcon} from 'src/components';
import {LeftButton, ModalCenterImage, SecondaryText} from 'src/components/primitives';
import {TSubject, channelIconHash} from 'src/constants';
import {TLicense} from 'src/containers/Licenses/definitions';
import {TAvailableTariffResponse, TariffPricing} from 'src/containers/Tariffs';
import {ExampleBoardImage} from 'src/images';
import {parseISO as customParseISO, joiResolver} from 'src/utils';
import {invariant} from 'src/utils/invariant';

import {useRescheduleCancelLicenseMutation, useUnscheduleCancelLicenseMutation} from '../../api';
import {TSubjectLicense} from './definitions';
import * as S from './style';

const MIN_DATE = subDays(new Date(), 1);

const ConfirmCancelModal = React.memo<{
  item: TLicense | TSubjectLicense;
  closeModal: TAnyFunction;
}>(({item, closeModal}) => {
  const {t} = useTranslation();

  const [unscheduleLicense] = useUnscheduleCancelLicenseMutation();

  return (
    <Modal width="430px">
      <ModalHeader>{t('Licenses:confirmCancelModal.title')}</ModalHeader>
      <ModalContent>
        <SecondaryText>{t('Licenses:confirmCancelModal.description')}</SecondaryText>
      </ModalContent>
      <ModalCenterImage as={ExampleBoardImage} />
      <ModalActions>
        <LeftButton onClick={closeModal} appearance="secondary">
          {t('Common:button.back')}
        </LeftButton>
        <Button
          onClick={() => {
            unscheduleLicense(item.id);
            closeModal();
          }}
          appearance="primary"
        >
          {t('Licenses:confirmCancelModal.button')}
        </Button>
      </ModalActions>
    </Modal>
  );
});

type TFormData = {
  scheduledDate: Date;
  newDate: Date | string;
};

const ChangeModal = React.memo<{
  item: TLicense | TSubjectLicense;
  closeModal: TAnyFunction;
}>(({item, closeModal}) => {
  const {t} = useTranslation();

  invariant('can only change scheduled cancel license', item.scheduledCancelAt);

  const [rescheduleLicense] = useRescheduleCancelLicenseMutation();

  const methods = useForm<TFormData>({
    defaultValues: {
      scheduledDate: parseISO(item.scheduledCancelAt),
      newDate: item.activeUntil ? parseISO(item.activeUntil) : undefined,
    },
    resolver: joiResolver((joi) =>
      joi.object({
        newDate: joi.date().required(),
      }),
    ),
  });

  const handleConfirm = React.useCallback(
    (values: TFormData) => {
      rescheduleLicense({licenseId: item.id, cancelAt: values.newDate});
      closeModal();
    },
    [rescheduleLicense, closeModal],
  );

  return (
    <FormProvider {...methods}>
      <Modal width="430px">
        <ModalHeader>{t('Licenses:changeModal.title')}</ModalHeader>
        <ModalContent>
          <Stack size="4">
            <SecondaryText>{t('Licenses:changeModal.description')}</SecondaryText>
            <Stack size="2">
              <S.SmallLabel>{t('Licenses:changeModal.scheduledDate')}</S.SmallLabel>
              <NewDatePickerField name="scheduledDate" disabled />
            </Stack>
            <Stack size="2">
              <S.SmallLabel>{t('Licenses:changeModal.newDate')}</S.SmallLabel>
              <NewDatePickerField
                minDate={MIN_DATE}
                maxDate={item.activeUntil ? parseISO(item.activeUntil) : undefined}
                name="newDate"
              />
            </Stack>
            <SecondaryText>{t('Licenses:changeModal.hint')}</SecondaryText>
          </Stack>
        </ModalContent>
        <ModalActions>
          <LeftButton onClick={closeModal} appearance="secondary">
            {t('Common:button.cancel2')}
          </LeftButton>
          <Button onClick={methods.handleSubmit(handleConfirm)} appearance="primary">
            {t('Common:button.toPlan')}
          </Button>
        </ModalActions>
      </Modal>
    </FormProvider>
  );
});

type TTariffModalProps = {tariff: TAvailableTariffResponse; subject: TSubject};

const TariffModal = React.memo<TTariffModalProps>(({tariff, subject}) => {
  const {t} = useTranslation();

  return (
    <Modal width="830px">
      <ModalHeader>{t('Licenses:tariffModal.title', {name: tariff.active.name})}</ModalHeader>
      <Stack size="2" isWrap>
        <S.Detail>
          <S.DetailLabel>{t('Licenses:tariffModal.tariff')}</S.DetailLabel>
          {tariff.active.name}
          {!!tariff.planned && (
            <>
              <StatusIcon name="warning" />
              <S.DetailLabel>
                {t('Licenses:tariffModal.willChange', {
                  date: customParseISO(tariff.planned.activeFrom, 'DATE'),
                })}
              </S.DetailLabel>
            </>
          )}
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>{t('Licenses:tariffModal.channel')}</S.DetailLabel>
          <Icon size="24px" as={channelIconHash[tariff.active.channelType]} />
          {subject?.name}
        </S.Detail>
        <S.Detail>
          <S.DetailLabel>{t('Licenses:tariffModal.activeFrom')}</S.DetailLabel>
          {customParseISO(tariff.active.activeFrom, 'DATE')}
        </S.Detail>
        <TariffPricing tariff={tariff.active} subjectId={subject.id} />
      </Stack>
    </Modal>
  );
});

TariffModal.displayName = 'TariffModal';

const showTariffModal = ({tariff, subject}: TTariffModalProps) =>
  showModal(() => <TariffModal tariff={tariff} subject={subject} />);

export {ChangeModal, ConfirmCancelModal, showTariffModal};
